import React, { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import GenericModal from '~/components/GenericModal/GenericModal';
import { BuildingEmailCsvPreview, postBuildingEmailsCsv } from '~/api/building';
import classes from '~/components/BuildingBulkImport/BuildingBulkImport.module.css';

const TEMPLATE_HEADERS = [
  'Building Name',
  'Email Type',
  'Email Address',
  'Role',
  'First Name',
  'Last Name',
  'Title'
];

const STATUS_LABEL = {
  add: '✅ Will add',
  update: '✏️ Will update names',
  invalid: '❌ Invalid'
};

interface IProps {
  onClose: () => void;
  onUploaded: () => void;
}

const BuildingEmailsCsvUpload = ({ onClose, onUploaded }: IProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BuildingEmailCsvPreview | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const send = async (dryRun: boolean) => {
    if (!file) {
      return;
    }
    setIsBusy(true);
    try {
      const { data } = await postBuildingEmailsCsv(file, dryRun);
      if (dryRun) {
        setPreview(data);
        return;
      }
      toast.success(
        `Added ${data.stats.toAdd} and updated ${data.stats.toUpdate} emails`
      );
      onUploaded();
      onClose();
    } catch (error: any) {
      // 400/500 are already toasted by the API client.
      if (error?.status !== 400 && error?.status !== 500) {
        toast.error(error?.message || 'Could not read the CSV');
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <GenericModal
      showModal
      title='Upload Building Emails from CSV'
      width='960px'
      enableOverflowScrolls
      dismissOnTapOutside={false}
      cancelOverride='Close'
      onChange={onClose}
      actionText={isBusy ? 'Saving…' : 'Upload'}
      actionFunction={() => send(false)}
      actionButtonDisabled={!preview?.canUpload || isBusy}
      content={
        <div>
          <p className={classes.intro}>
            One email per row, for any buildings. Email Type is report or group.
            Role is a report role (Owner, On-Site, Regional, Marketing) or a
            group (Reports, Reviews, Referrals, Flyers). Existing emails are
            kept. A matching email and role gets its names updated. Blank name
            cells keep the saved names.
          </p>
          {/* Hidden: a global input[type="file"] rule hides it anyway; the drop zone opens it. */}
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv,text/csv'
            style={{ display: 'none' }}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setPreview(null);
              // Lets the same (edited) file fire onChange when picked again.
              e.target.value = '';
            }}
          />
          <div className={classes.uploadRow}>
            <div
              role='button'
              tabIndex={0}
              className={classes.dropZone}
              onClick={() => !isBusy && fileInputRef.current?.click()}
              onKeyDown={(e) =>
                e.key === 'Enter' && !isBusy && fileInputRef.current?.click()
              }
            >
              <span className={classes.fileName}>
                {file ? `📄 ${file.name}` : 'Click to choose a CSV'}
              </span>
              <span className={classes.hint}>
                Then press Validate. Upload unlocks once every row passes.
              </span>
            </div>
            <button
              type='button'
              className='btn-standard btn-primary'
              disabled={!file || isBusy}
              onClick={() => send(true)}
            >
              Validate
            </button>
          </div>
          <div className={classes.links}>
            <CSVLink
              data={[TEMPLATE_HEADERS]}
              filename='building-emails-template.csv'
              className={classes.link}
            >
              Download template
            </CSVLink>
          </div>
          {preview && (
            <div className={classes.section}>
              <div className={classes.stats}>
                <span>Total: {preview.stats.total}</span>
                <span>Will add: {preview.stats.toAdd}</span>
                <span>Will update: {preview.stats.toUpdate}</span>
                <span>Invalid: {preview.stats.invalid}</span>
              </div>
              {preview.missingColumns.length > 0 && (
                <span className={classes.error}>
                  Missing columns: {preview.missingColumns.join(', ')}
                </span>
              )}
              <div className={classes.tableWrap}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>Sheet row</th>
                      <th>Building</th>
                      <th>Type</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Problems</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.rowNumber}>
                        <td>{row.rowNumber}</td>
                        <td>{row.buildingName}</td>
                        <td>{row.emailType}</td>
                        <td>{row.email}</td>
                        <td>{row.role}</td>
                        <td>{STATUS_LABEL[row.status]}</td>
                        <td>{row.errors.join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      }
    />
  );
};

export default BuildingEmailsCsvUpload;
